import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, decompressData, genApiUrl } from '../../util/clientApiHelpers.js';
import { TempChatTurn } from '#shared/domain/chat/ChatInterfaces.js';
import { MODULE_NAMES } from '#shared/config/constants.js';
import { Payload } from '#shared/util/apiHelpers.js';
import { TempChatResponse } from '#shared/api/ModuleResponse.js';

/**
 * A client-side hook for interacting with the TEMP_CHAT API endpoints.
 */
export const useTempChatApi = () => {
	const queryClient = useQueryClient();

	/**
	 * Saves a temporary chat turn object.
	 * REFACTORED: Now expects an object { tempTurnId: string } from the server.
	 */
	const saveTempChatTurn = useMutation<{ tempTurnId: string }, Error, TempChatTurn>({
		mutationFn: async (tempData) => {
			const url = genApiUrl(MODULE_NAMES.TEMP, 'saveTempChatTurn');
			const response = await apiClient.post<{ tempTurnId: string }>(url, tempData);
			return response.data;
		},
		onSuccess: (data, variables) => {
			// After saving a temp turn, invalidate the query for that specific turn.
			// This ensures that any component displaying this turn will get the latest data,
			// including the server-generated tempTurnId, createdAt, and updatedAt fields.
			queryClient.invalidateQueries({
				queryKey: ['getTempChatTurn', variables.sessionId, variables.sequence],
			});
		},
	});

	/**
	 * Fetches a temporary chat turn.
	 */
	const getTempChatTurn = (sessionId: string, sequence: number, isHistoryLoading: boolean) =>
		useQuery<TempChatResponse, Error>({
			queryKey: ['getTempChatTurn', sessionId, sequence],
			queryFn: async () => {
				const url = genApiUrl(MODULE_NAMES.TEMP, 'getTempChatTurn', [sessionId, sequence]);
				const response = await apiClient.get<Payload>(url, {
					_suppressToast: true,
					_suppress404Error: true,
				});
				return decompressData<TempChatResponse>(response.data.payload);
			},
			enabled: !isHistoryLoading && !!sessionId && sequence >= 0,
			retry: false,
		});

	return { saveTempChatTurn: saveTempChatTurn.mutateAsync, getTempChatTurn };
};
